import { router } from '@inertiajs/react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';

import SearchInput from '@/theme2/components/search/SearchInput';
import SearchResults from '@/theme2/components/search/SearchResults';
import SearchSuggestions from '@/theme2/components/search/SearchSuggestions';
import EmptySearch from '@/theme2/components/search/EmptySearch';
import SearchFooter from '@/theme2/components/search/SearchFooter';

import { useSearch } from '@/theme2/hooks/useSearch';
import { useRecentSearches } from '@/theme2/hooks/useRecentSearches';

export default function SearchDialog({ open, onClose }) {
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);

    const {
        results,
        groupedResults,
        hasQuery,
        trendingSearches,
        searchCategories,
    } = useSearch(query);
    const { recentSearches, saveSearch, clearSearches } = useRecentSearches();

    const flatResults = useMemo(
        () => Object.values(groupedResults).flat(),
        [groupedResults],
    );

    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveIndex((current) =>
                    Math.min(current + 1, Math.max(flatResults.length - 1, 0)),
                );
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveIndex((current) => Math.max(current - 1, 0));
            }

            if (event.key === 'Enter' && flatResults[activeIndex]) {
                saveSearch(query);
                router.visit(flatResults[activeIndex].url);
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeIndex, flatResults, onClose, open, query, saveSearch]);

    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    const handleSelect = () => {
        saveSearch(query);
        onClose();
    };

    const handleSearchPick = (value) => {
        setQuery(value);
        saveSearch(value);
    };

    return (
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-50">
                    <motion.button
                        type="button"
                        aria-label="Close search"
                        onClick={onClose}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/40 backdrop-blur-md"
                    />

                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        aria-label="Global search"
                        initial={{ opacity: 0, scale: 0.96, y: 24 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 24 }}
                        transition={{ duration: 0.28, ease: 'easeOut' }}
                        className="absolute inset-x-3 top-3 max-h-[calc(100vh-1.5rem)] overflow-hidden rounded-[2rem] border border-border bg-white shadow-2xl shadow-black/25 sm:inset-x-6 sm:top-10 md:right-auto md:left-1/2 md:w-full md:max-w-3xl md:-translate-x-1/2 lg:max-w-4xl"
                    >
                        <div className="p-4 sm:p-6">
                            <SearchInput
                                value={query}
                                onChange={setQuery}
                                onClear={() => setQuery('')}
                            />

                            <div className="mt-6 max-h-[60vh] overflow-y-auto pr-1">
                                {hasQuery && results.length > 0 && (
                                    <SearchResults
                                        groupedResults={groupedResults}
                                        query={query}
                                        activeIndex={activeIndex}
                                        onSelect={handleSelect}
                                    />
                                )}

                                {hasQuery && results.length === 0 && (
                                    <EmptySearch
                                        query={query}
                                        onSearch={handleSearchPick}
                                        onSelect={onClose}
                                        trendingSearches={trendingSearches}
                                        searchCategories={searchCategories}
                                    />
                                )}

                                {!hasQuery && (
                                    <SearchSuggestions
                                        recentSearches={recentSearches}
                                        onSearch={handleSearchPick}
                                        onClearRecent={clearSearches}
                                        onSelect={onClose}
                                        trendingSearches={trendingSearches}
                                        searchCategories={searchCategories}
                                    />
                                )}
                            </div>
                        </div>

                                <SearchFooter />
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
